// defaultTheme.js

/**
 * Builtin Ai default theme pack (Google News-aligned palette).
 *
 * Pack shape: { id, version, modes: { DARK, LIGHT } }
 *
 * ## Mapping layer (Theme Editor ↔ this file ↔ CSS)
 *
 * The catalog in `themeTokenSchema.js` is the single mapping layer. Each token has:
 *   - `id`     — object key here (DARK_MODE / LIGHT_MODE) and in Firestore packs
 *   - `title`  — label shown in Studio Theme Designer
 *   - `group`  — inspector section (Surfaces, Text, Buttons, …)
 *
 * There is no separate translation table. The `id` is the wire everywhere:
 *
 *   themeTokenSchema id  →  modes.DARK|LIGHT[id]  →  theme[id] (JS)  →  var(--id) (CSS)
 *
 * Flow:
 *   1. StudioThemeDesigner reads THEME_COLOR_TOKENS, displays `title`, reads/writes `id`
 *   2. resolveTheme() / themePackModel merge custom packs over these defaults
 *   3. themeVariables.buildThemeCssVariables() publishes `var(--{id})` on :root
 *
 * Recipes (glass, elevationShadow, focusRing, heroGlow, …) are NOT stored here —
 * they are computed in themeVariables.js from editable tokens. See THEME_RECIPES.
 *
 * ## Editor label → key in DARK_MODE / LIGHT_MODE
 *
 * Surfaces:
 *   App background      → background
 *   Mobile background   → backgroundMobile
 *   Faded surface       → backgroundFaded
 *   Muted surface       → backgroundMuted
 *   Hover surface       → backgroundHover
 *   Selected surface    → backgroundSelected
 *   Panel               → panel
 *   Popup               → popup
 *   Overlay scrim       → overlay
 *   Input field         → input
 *   Status bar          → status
 *
 * Text:
 *   Primary text              → foreground
 *   Secondary text            → foregroundFaded
 *   Muted text                → foregroundMuted
 *   Selected text             → foregroundSelected
 *   Outline                   → outline
 *   Muted outline             → outlineMuted
 *   Message text              → message
 *   Text selection background → selectionBackground
 *   Text selection text       → selectionForeground
 *
 * Buttons:
 *   Button background       → buttonBackgroundColor
 *   Button label            → buttonLabelColor
 *   Button icon             → buttonIconColor
 *   Button hover background → buttonHoverBackgroundColor
 *   Button hover label      → buttonHoverLabelColor
 *   Button hover icon       → buttonHoverIconColor
 *
 * Accent:
 *   Accent             → highlight
 *   Accent hover       → highlightHover
 *   Accent foreground  → highlightForeground
 *   Accent glow        → highlightGlow
 *   Glow               → glow
 *
 * Status:
 *   Success / Error / Warning / Active → success, error, warning, active
 *   Red / Orange / Yellow / Green / Teal / Blue / Purple → same id
 *
 * Orbit:
 *   Orbit ring            → orbitRing
 *   Orbit ring muted      → orbitRingMuted
 *   Orbit accent glow     → orbitHighlightGlow
 *   Orbit secondary dot   → orbitDotSecondary
 *   Orbit secondary glow  → orbitDotSecondaryGlow
 *
 * Meta (on mode objects, not in the editor color grid):
 *   key, colorOptions, enableBackground
 *
 * See also: docs/archive/studio/theme-tokens-v1-spec.md
 */

export const ThemeMode = Object.freeze({
  DARK: 'DARK',
  LIGHT: 'LIGHT',
});

/**
 * Shared accent swatches (ColorButton, scheduler cards, lane colors, etc.).
 *
 * Tuned for white label text (~≥3:1) and dual light/dark use — mid-range only
 * (no pastels, no near-black). Indices 0–7 keep Google News brand hues for
 * marketing export; gold replaces the former light yellow.
 *
 * @see https://partnermarketinghub.withgoogle.com/brands/google-news/visual-identity/color-palette/
 */
export const BASE_COLOR_OPTIONS = Object.freeze([
  // Google-aligned (marketing indices 0–7)
  '#3a83ef', // Blue
  '#A50E0E', // Red
  '#E37400', // Orange
  '#0D652D', // Green
  '#4285F4', // Medium blue
  '#EA4335', // Medium red
  '#B8860B', // Gold
  '#34A853', // Medium green

  // Reds → oranges
  '#C62828', // Red
  '#D32F2F', // Red
  '#E53935', // Red
  '#D84315', // Deep orange
  '#E64A19', // Deep orange
  '#F4511E', // Deep orange
  '#EF6C00', // Orange
  '#C16515', // Orange
  '#B36119', // Amber
  '#A5731D', // Amber

  // Golds → greens
  '#9C821C', // Gold
  '#8F7614', // Gold
  '#7F931A', // Olive
  '#6B931A', // Olive
  '#519C1C', // Lime
  '#329A1D', // Lime
  '#2E7D32', // Green
  '#43A047', // Green
  '#689F38', // Light green
  '#558B2F', // Olive

  // Teals → cyans
  '#00695C', // Teal
  '#00796B', // Teal
  '#00897B', // Teal
  '#00838F', // Cyan
  '#0097A7', // Cyan
  '#1A919C', // Cyan

  // Skies → blues
  '#0277BD', // Sky
  '#0288D1', // Sky
  '#039BE5', // Sky
  '#1565C0', // Blue
  '#1976D2', // Blue
  '#1E88E5', // Blue

  // Indigos → violets
  '#3949AB', // Indigo
  '#5C6BC0', // Indigo
  '#5E35B1', // Deep purple
  '#7E57C2', // Deep purple
  '#7B1FA2', // Purple
  '#8E24AA', // Purple
  '#AB47BC', // Purple

  // Magentas → roses
  '#C2185B', // Pink
  '#AD1457', // Pink
  '#D81B60', // Pink
  '#E91E63', // Pink
  '#C6285C', // Rose

  // Neutrals
  '#6D4C41', // Brown
  '#795548', // Brown
  '#8D6E63', // Brown
  '#455A64', // Blue grey
  '#546E7A', // Blue grey
  '#607D8B', // Blue grey
  '#78909C', // Blue grey
  '#616161', // Grey
  '#757575', // Grey
]);

const DARK_MODE = Object.freeze({
  key: ThemeMode.DARK,

  background: '#111111', // App background
  backgroundMobile: '#111111', // Mobile background
  backgroundFaded: '#191919', // Faded surface
  backgroundMuted: '#1c1c1c', // Muted surface
  backgroundHover: '#222222cc', // Hover surface
  backgroundSelected: '#222222cc', // Selected surface
  panel: 'rgba(255, 255, 255, 0.02)', // Panel
  popup: '#222222cc', // Popup
  overlay: '#222222aa', // Overlay scrim
  input: '#222222',
  status: 'rgba(69, 69, 69, 0.92)',

  // TEXT
  foreground: '#E8EAED', // Primary text
  foregroundFaded: '#BDC1C6', // Secondary text
  foregroundMuted: '#9AA0A6', // Muted text
  foregroundSelected: '#E8EAED', // Selected text
  outline: 'rgba(232, 234, 237, 0.14)', // Outline
  outlineMuted: 'rgba(232, 234, 237, 0.08)', // Muted outline
  message: '#E8EAED', // Message text
  selectionBackground: 'rgba(69, 69, 69, 0.92)', // Text selection background
  selectionForeground: '#ffffff', // Text selection text

  // BUTTONS
  buttonBackgroundColor: '#3872e0', // Button background
  buttonLabelColor: '#FFFFFF', // Button label
  buttonIconColor: '#FFFFFF', // Button icon
  buttonHoverBackgroundColor: '#5f91ec', // Button hover background
  buttonHoverLabelColor: '#FFFFFF', // Button hover label
  buttonHoverIconColor: '#FFFFFF', // Button hover icon
  
  // ACCENT
  highlight: '#3872e0', // Accent
  highlightHover: '#5f91ec', // Accent hover
  highlightForeground: '#FFFFFF', // Accent foreground
  highlightGlow: '#221e2011', // Accent glow
  glow: '#221e2011', // Glow


  success: '#34A853', // Success
  error: '#EA4335', // Error
  active: '#8AB4F8',
  warning: '#E37400', // Warning
  red: '#de2312', // Red
  purple: '#583f92', // Purple
  blue: '#234170', // Blue
  green: '#34A853', // Green
  teal: '#178796', // Teal
  orange: '#E37400', // Orange
  yellow: '#9c7707', // Yellow
  
  // ORBIT
  orbitRing: '#ffffff09', // Orbit ring
  orbitRingMuted: '#ffffff09', // Orbit ring muted
  orbitHighlightGlow: '#ffffff09', // Orbit accent glow
  orbitDotSecondary: '#34A853', // Orbit secondary dot
  orbitDotSecondaryGlow: 'rgba(52, 168, 83, 0.4)', // Orbit secondary glow

  // ENABLE BACKGROUND
  enableBackground: true,

  // COLOR OPTIONS
  colorOptions: BASE_COLOR_OPTIONS,
});

const LIGHT_MODE = Object.freeze({
  key: ThemeMode.LIGHT,

  // SURFACES
  background: '#FFFFFF', // App background
  backgroundMobile: '#FFFFFF', // Mobile background
  backgroundFaded: '#f8f9fa', // Faded surface
  backgroundMuted: '#f8f9fa', // Muted surface
  backgroundHover: '#f8f9fa', // Hover surface
  backgroundSelected: 'rgba(32, 33, 36, 0.04)', // Selected surface
  panel: 'rgba(0, 0, 0, 0.02)', // Panel
  popup: 'rgba(255, 255, 255, 0.95)', // Popup
  overlay: '#f8f9fa', // Overlay scrim
  input: '#f8f9fa', // Input field
  status: 'rgba(32, 33, 36, 0.14)', // Status bar

  // TEXT
  foreground: '#000000', // Primary text
  foregroundFaded: '#6b6b6b', // Secondary text
  foregroundMuted: '#7d7d7d', // Muted text
  foregroundSelected: '#242424', // Selected text
  outline: 'rgba(32, 33, 36, 0.14)', // Outline
  outlineMuted: 'rgba(32, 33, 36, 0.08)', // Muted outline
  message: '#202124', // Message text
  selectionBackground: '#3872e0', // Text selection background
  selectionForeground: '#FFFFFF', // Text selection text
  
  // BUTTONS
  buttonBackgroundColor: '#3872e0', // Button background
  buttonLabelColor: '#FFFFFF', // Button label
  buttonIconColor: '#FFFFFF', // Button icon
  buttonHoverBackgroundColor: '#5f91ec', // Button hover background
  buttonHoverLabelColor: '#FFFFFF', // Button hover label
  buttonHoverIconColor: '#FFFFFF', // Button hover icon

  // ACCENT
  highlight: '#3872e0', // Accent
  highlightHover: '#5f91ec', // Accent hover
  highlightForeground: '#FFFFFF', // Accent foreground
  highlightGlow: '#f8f9fa', // Accent glow
  glow: '#f8f9fa',

  // ORBIT
  orbitRing: '#f8f9fa', // Orbit ring
  orbitRingMuted: '#f8f9fa22', // Orbit ring muted
  orbitHighlightGlow: 'rgba(13, 13, 13, 0.28)', // Orbit accent glow
  orbitDotSecondary: '#34A853', // Orbit secondary dot
  orbitDotSecondaryGlow: 'rgba(52, 168, 83, 0.45)', // Orbit secondary glow
  
  // ENABLE BACKGROUND
  enableBackground: true,

  // COLOR OPTIONS
  colorOptions: BASE_COLOR_OPTIONS,
});

export const ORBITAL_DEFAULT_PACK = Object.freeze({
  id: 'orbital-default',
  version: 1,
  modes: Object.freeze({
    [ThemeMode.DARK]: DARK_MODE,
    [ThemeMode.LIGHT]: LIGHT_MODE,
  }),
});
